import { Card } from "./game/Card.js";
import { Vinyl } from "./game/Vinyl.js";

/**
 * The background every screen outside a game shares: the game itself, out of
 * focus, lit.
 *
 * Started life on the front door only, and moved here the moment the account
 * screens wanted it — a page walking from the landing to "sign in" and finding
 * flat black had the app changing identity mid-flow, which reads as two
 * products stitched together.
 *
 * # Why the board is built rather than photographed
 *
 * The obvious version of "a blurred screenshot of the board" is a PNG. This
 * composes the **real** `Card` and `Vinyl` components instead, for three
 * reasons that all point the same way:
 *
 * - it cannot go stale — restyle a card and this restyles with it, where a
 *   screenshot quietly becomes a picture of last month's game;
 * - it themes — every colour here comes from the same tokens as the board, so
 *   changing the tone changes the backdrop too, with nothing to re-export;
 * - it costs no bytes. A blurred 1440p PNG is 200–400 kB on the one screen a
 *   guest opens on mobile data at somebody's house.
 *
 * # ⚠️ The board alone is not a background, and the first version proved it
 *
 * A card is `bg-stage-900` (`#120f26`) on a `stage-950` (`#070612`) page — six
 * points of lightness apart, because that is what makes a board readable in a
 * dim room. Blur that, drop it to 28% opacity, then put a 75%-opaque scrim over
 * it, and roughly **7%** of an already-near-black shape survives: the page reads
 * as flat black with a faint smudge in the middle. Measured by shipping it.
 *
 * So the colour comes from a separate layer, and the board rides on top of it as
 * *texture* rather than as the source of light. That is also the honest
 * division: cards are dark by design and should stay that way; a landing page is
 * allowed to be lit.
 *
 * # The colour field is gradients, not blurred elements
 *
 * The usual "aurora" trick is half a dozen divs with `blur-[120px]`. Each one is
 * a real filter pass over a large area, and this is the first screen on a
 * stranger's phone. `radial-gradient` costs nothing by comparison and is
 * indistinguishable at this softness, so there is exactly **one** `filter` on
 * this page — the board's — and everything else is paint.
 *
 * # It is decoration and nothing else
 *
 * `aria-hidden`, `pointer-events-none`, no state. A screen reader gets the
 * headline and the two panels; this contributes nothing to them.
 */
export function BoardBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
      {/* 1. The light. Four stops of the brand ramp, placed so the cool end is
             high and the hot end low — the same order the gradient runs in, so
             the page and the wordmark agree about which way is which. */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: [
            "radial-gradient(55rem 40rem at 12% -5%, rgb(41 197 255 / 0.22), transparent 65%)",
            "radial-gradient(50rem 38rem at 78% 0%, rgb(59 107 255 / 0.26), transparent 65%)",
            "radial-gradient(60rem 45rem at 95% 55%, rgb(138 63 245 / 0.28), transparent 65%)",
            "radial-gradient(55rem 40rem at 8% 85%, rgb(255 45 180 / 0.20), transparent 65%)",
            "radial-gradient(45rem 35rem at 55% 110%, rgb(138 63 245 / 0.22), transparent 65%)",
          ].join(","),
        }}
      />

      {/* 2. The board, out of focus.
             Wider than the viewport and rotated a few degrees, because a row of
             cards stopping dead in the middle of a 2000px screen reads as a
             component that failed to fill rather than as a table somebody is
             playing on. */}
      <div className="absolute -inset-x-[15%] inset-y-0 opacity-[0.55] blur-[14px]">
        <div className="flex h-full -rotate-[4deg] flex-col justify-center gap-14">
          <Row cards={TOP_ROW} offset="-6%" />

          {/* The record, mid-clip. Not spinning: this sits under text somebody is
              reading, and a rotating disc there is the definition of motion
              nobody asked for. */}
          <div className="flex justify-center">
            <Vinyl size={260} />
          </div>

          <Row cards={BOTTOM_ROW} offset="4%" />
        </div>
      </div>

      {/* 3. The scrim — far lighter than the first attempt, and shaped rather
             than flat. Heaviest behind the middle column where the headline is,
             and it lets the colour through at the edges instead of erasing it.
             The panels carry their own `bg-stage-900/70 backdrop-blur`, so the
             text that most needs contrast is not relying on this. */}
      <div className="absolute inset-0 bg-stage-950/45" />
      <div className="absolute inset-0 bg-[radial-gradient(45rem_50rem_at_50%_50%,rgb(7_6_18/0.72),rgb(7_6_18/0.25)_70%,transparent)]" />
    </div>
  );
}

/** One row of the out-of-focus board. */
function Row({ cards, offset }: { cards: readonly BackdropCard[]; offset: string }) {
  return (
    <div className="flex justify-center gap-6" style={{ marginLeft: offset }}>
      {cards.map((card) => (
        <Card
          key={card.title}
          artist={card.artist}
          title={card.title}
          year={card.year}
          state={card.state}
          compact
        />
      ))}
    </div>
  );
}

type BackdropCard = {
  artist: string;
  title: string;
  year: number | null;
  state: "placed" | "unknown" | "revealed";
};

/**
 * Invented tracks, deliberately.
 *
 * Real catalog titles here would be a claim about what a party will be dealt,
 * and the catalog changes. Nothing is legible through the blur; these exist to
 * give the cards the right shape, and the years to give them the right rhythm —
 * ascending, with one still unknown, which is what a board mid-round looks like.
 */
const TOP_ROW: readonly BackdropCard[] = [
  { artist: "—", title: "Cold Arcade", year: 1969, state: "placed" },
  { artist: "—", title: "Neon Hours", year: 1978, state: "placed" },
  { artist: "—", title: "Paper Radio", year: 1984, state: "placed" },
  { artist: "—", title: "Slow Transit", year: null, state: "unknown" },
  { artist: "—", title: "Harbour Lights", year: 1991, state: "placed" },
  { artist: "—", title: "Static Bloom", year: 2004, state: "placed" },
  { artist: "—", title: "Night Bus", year: 2018, state: "placed" },
];

const BOTTOM_ROW: readonly BackdropCard[] = [
  { artist: "—", title: "Ultraviolet", year: 1996, state: "revealed" },
  { artist: "—", title: "Long Player", year: 2011, state: "placed" },
  { artist: "—", title: "Tape Hiss", year: 1974, state: "placed" },
  { artist: "—", title: "Wire & Glass", year: 1988, state: "placed" },
  { artist: "—", title: "Second Sun", year: 2007, state: "placed" },
  { artist: "—", title: "Low Orbit", year: 1965, state: "placed" },
];
